import React, { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, XIcon } from 'lucide-react';
import { KindTag } from './KindTag';
import { useLanguage } from '../../contexts/LanguageContext';
import { whatsappLink } from '../../utils/whatsapp';
import type { Project } from '../../hooks/useProjects';
type LightboxProps = {
  items: Project[];
  index: number | null;
  onClose: () => void;
  onChange: (index: number) => void;
};
export function Lightbox({
  items,
  index,
  onClose,
  onChange
}: LightboxProps) {
  const {
    t
  } = useLanguage();
  const labels = t.portfolio.lightbox;
  const open = index !== null && items[index] !== undefined;
  const current = open ? items[index as number] : null;
  const go = (dir: 1 | -1) => {
    if (index === null) return;
    onChange((index + dir + items.length) % items.length);
  };
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') go(1);
      if (e.key === 'ArrowLeft') go(-1);
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, index]);
  return <AnimatePresence data-id="element-339" data-mp="2phbrzrs4c5p6">
      {current ? <motion.div role="dialog" aria-modal="true" aria-label={current.title} className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/90 p-4 sm:p-8" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: [0.23, 1, 0.32, 1]
    }} onClick={onClose} data-id="element-340" data-mp="2lhjer1rs5bdh">
          <button type="button" onClick={onClose} aria-label={labels.close} className="absolute right-4 top-4 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white transition-colors duration-150 ease-smooth hover:bg-white/20" data-id="element-341" data-mp="348av4na6q21d">
            <XIcon className="h-5 w-5" data-id="element-342" data-mp="3l8vm6ph102rh" />
          </button>

          <div className="flex w-full max-w-5xl flex-col items-center gap-5 lg:flex-row lg:items-end" onClick={e => e.stopPropagation()} data-id="element-343" data-mp="3l7c4qvjmvlo4">
            <motion.img key={current.id} src={current.image ?? ''} alt={current.title} className="max-h-[62vh] w-auto max-w-full cursor-grab rounded-xl object-contain active:cursor-grabbing lg:max-h-[80vh] lg:max-w-[65%]" initial={{
          opacity: 0,
          scale: 0.97
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.2,
          ease: [0.23, 1, 0.32, 1]
        }} drag="x" dragConstraints={{
          left: 0,
          right: 0
        }} dragElastic={0.3} onDragEnd={(_, info) => {
          if (info.offset.x < -60) go(1);else if (info.offset.x > 60) go(-1);
        }} data-id="element-344" data-mp="3uyowln5fmbu5" />
            <div className="w-full text-white lg:max-w-sm lg:pb-2" data-id="element-345" data-mp="1xbe0rvldld51">
              <KindTag kind={current.kind} label={t.common.kinds[current.kind]} data-id="element-346" data-mp="1xbnp7eqrvc8m" />
              <h2 className="mt-3 font-display text-2xl font-semibold" data-id="element-347" data-mp="1sdmaf60dpz2y">{current.title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-white/75" data-id="element-348" data-mp="2pyqm62fsvewr">{current.description}</p>
              <div className="mt-5 flex flex-wrap items-center gap-3" data-id="element-349" data-mp="2l20459kfq98p">
                <a href={whatsappLink(`${labels.whatsappPrefix} ${current.title}`)} target="_blank" rel="noreferrer" className="rounded-full bg-white px-5 py-3 text-sm font-medium text-mauve-deep transition-colors duration-150 ease-smooth hover:bg-petal" data-id="element-350" data-mp="2g1ch0yzbucjr">
                  {labels.book}
                </a>
                {items.length > 1 ? <div className="flex items-center gap-2" data-id="element-351" data-mp="2og8hcuizkia0">
                    <button type="button" onClick={() => go(-1)} aria-label={labels.prev} className="flex h-12 w-12 items-center justify-center rounded-full border border-white/25 transition-colors duration-150 ease-smooth hover:bg-white/10" data-id="element-352" data-mp="2t91mvkma3un5">
                      <ChevronLeftIcon className="h-5 w-5" data-id="element-353" data-mp="3792k8q3c4jxv" />
                    </button>
                    <button type="button" onClick={() => go(1)} aria-label={labels.next} className="flex h-12 w-12 items-center justify-center rounded-full border border-white/25 transition-colors duration-150 ease-smooth hover:bg-white/10" data-id="element-354" data-mp="3c6vtjrzxpu2d">
                      <ChevronRightIcon className="h-5 w-5" data-id="element-355" data-mp="1qtsx5kz6d6ir" />
                    </button>
                    <span className="ml-1 text-xs text-white/60" data-id="element-356" data-mp="8w63be0tueiu">
                      {(index as number) + 1} {labels.of} {items.length}
                    </span>
                  </div> : null}
              </div>
            </div>
          </div>
        </motion.div> : null}
    </AnimatePresence>;
}